import { FC, useContext, useMemo, useState, useEffect, useCallback, useRef } from 'react';
import {
	Box,
	Text,
	Button,
	Flex,
	Avatar,
} from 'src/components/ui';
import {
	ICreative,
	isCreatomateAttributes,
} from 'src/lib/schemas/campaign/newFlowCampaign';
import UserContext from 'src/contexts/user/UserContext';
import CreativeCard from '../CreativeCard';
import { IChannelCreativeAttributes } from 'src/lib/schemas';
import { formatCallToAction } from '../utils/formatedCTA';
import FusionLoading from 'src/components/common/FusionLoading';
import TemplatePreview, { TemplatePreviewHandle } from 'src/components/templates/TemplatePreview';
import { FaPlay, FaPause, FaExpand } from 'react-icons/fa';
import VideoExpandModal from '../utils/VideoExpandModal';
import useSmoothProgress from '../utils/useSmoothProgress';

interface VideoSquareCreativeProps {
	creative: ICreative;
	withActions?: boolean;
	size?: string;
	creativesConfig?: IChannelCreativeAttributes[];
	isPaid?: boolean;
	selectedHeadline?: string;
	selectedDescription?: string;
}

const VideoSquareCreative: FC<VideoSquareCreativeProps> = ({
	creative,
	withActions = true,
	size = 'md',
	creativesConfig,
	selectedHeadline,
	selectedDescription,
}) => {
	const { account } = useContext(UserContext);
	const { callToAction, headlines, descriptions } = (creative as any).attributes;
	const headline = selectedHeadline ?? headlines?.[0];
	const description = selectedDescription ?? descriptions?.[0];
	const CTA = callToAction ?? 'Contact us';
	const [logo, setLogo] = useState<string | undefined>(account?.logoDark || account?.logoSquare);
	const previewRef = useRef<TemplatePreviewHandle>(null);
	const [previewReady, setPreviewReady] = useState(false);
	const [hasStarted, setHasStarted] = useState(false);
	const [isPlaying, setIsPlaying] = useState(false);
	const [isHovering, setIsHovering] = useState(false);
	const [duration, setDuration] = useState(10);
	const seekBarRef = useRef<HTMLDivElement>(null);
	const [isExpanded, setIsExpanded] = useState(false);
	const { progressBarRef, timeDisplayRef, onTimeChange, setTimeImmediate } = useSmoothProgress({ duration, isPlaying });

	const isPending =
		creative.status === 'pending' || creative.status === 'generating';

	const isCreatomate =
		creative.adEngine === 'creatomate' &&
		isCreatomateAttributes(creative.attributes);

	const videoTemplate = useMemo(() => {
		if (isCreatomate) {
			return JSON.parse(JSON.stringify(creative.attributes));
		}
		return null;
	}, [JSON.stringify((creative as any).attributes.elements)]);

	// Trust the template's composition duration over the SDK's getDuration(), which often returns
	// only the embedded video element's duration instead of the full composition length.
	const templateDuration = useMemo(() => {
		const attrs = (creative as any).attributes;
		if (!attrs) return 0;
		return attrs.duration || attrs.elements?.[0]?.duration || 0;
	}, [(creative as any).attributes?.duration, (creative as any).attributes?.elements?.[0]?.duration]);

	useEffect(() => {
		if (templateDuration > 0) {
			setDuration(templateDuration);
		}
	}, [templateDuration]);

	const onPreviewReady = useCallback(() => {
		setPreviewReady(true);
		setTimeout(() => {
			if (previewRef.current) {
				previewRef.current.setLoop(false);
				previewRef.current.setControls(false);
				previewRef.current.setTime(5);
				// Prefer template duration; fall back to SDK, then to 10s.
				setDuration(templateDuration || previewRef.current.getDuration() || 10);
			}
		}, 300);
	}, [templateDuration]);

	// If the SDK reports a time greater than our cached duration, the initial getDuration()
	// call returned a stale/partial value. Auto-correct so the progress bar stays accurate.
	const handleTimeChange = useCallback((time: number) => {
		if (time > duration + 0.1) {
			setDuration(time);
		}
		onTimeChange(time);
	}, [duration, onTimeChange]);

	const handlePlayPause = useCallback(() => {
		if (!previewRef.current) return;
		if (isPlaying) {
			previewRef.current.pause();
		} else {
			previewRef.current.play();
		}
	}, [isPlaying]);

	const handleInitialPlay = useCallback(() => {
		if (!previewRef.current) return;
		setHasStarted(true);
		previewRef.current.setTime(0);
		previewRef.current.play();
	}, []);

	const handleSeek = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
		if (!seekBarRef.current || !previewRef.current) return;
		const rect = seekBarRef.current.getBoundingClientRect();
		const ratio = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
		const newTime = ratio * duration;
		previewRef.current.setTime(newTime);
		setTimeImmediate(newTime);
	}, [duration, setTimeImmediate]);

	const onPlay = useCallback(() => setIsPlaying(true), []);
	const onPause = useCallback(() => setIsPlaying(false), []);

	useEffect(() => {
		setLogo(account?.logoDark || account?.logoSquare);
	}, [account]);

	return (
		<>
		<CreativeCard
			creative={creative}
			withActions={withActions}
			size={size}
			title="Video"
			creativesConfig={creativesConfig}
			isVideoCreative
		>
			{isPending ? (
				<Flex width="100%" minH={{ base: '400px', md: '677px' }} justifyContent="center">
					<FusionLoading isLoading />
				</Flex>
			) : (
				<>
					<Box p={4}>
						<Flex alignItems="center" gap={2}>
							<Avatar
								key={logo || 'no-logo'}
								src={logo}
								sx={{ img: { objectFit: 'contain' } }}
							/>
							<Box>
								<Text fontWeight="bold">{account?.name}</Text>
							</Box>
						</Flex>
					</Box>
					<Box
						borderRadius="5px"
						mx={4}
						h={{ base: '250px', md: '382px' }}
						position="relative"
						onMouseEnter={() => setIsHovering(true)}
						onMouseLeave={() => setIsHovering(false)}
					>
						{isCreatomate && videoTemplate ? (
							<>
								<Box filter={previewReady && !hasStarted ? 'brightness(0.7)' : undefined} h="100%">
									<TemplatePreview
										ref={previewRef}
										key={`${creative.id}-${(creative as any).videoAdReference || 'default'}`}
										template={videoTemplate}
										borderRadius="5px"
										onReady={onPreviewReady}
										onTimeChange={handleTimeChange}
										onPlay={onPlay}
										onPause={onPause}
									/>
								</Box>
								{/* Initial centered play button */}
								{previewReady && !hasStarted && (
									<Flex
										position="absolute"
										top={0}
										left={0}
										right={0}
										bottom={0}
										align="center"
										justify="center"
										zIndex={2}
										cursor="pointer"
										onClick={handleInitialPlay}
									>
										<Flex
											w="64px"
											h="64px"
											borderRadius="full"
											bg="blackAlpha.600"
											align="center"
											justify="center"
											boxShadow="lg"
										>
											<Box as={FaPlay} color="white" fontSize="24px" ml="4px" />
										</Flex>
									</Flex>
								)}
								{/* Video controls bar - on hover after started */}
								{hasStarted && isHovering && (
									<Flex
										position="absolute"
										bottom={0}
										left={0}
										right={0}
										bg="blackAlpha.700"
										px={3}
										py={2}
										align="center"
										gap={2}
										zIndex={2}
										borderBottomRadius="5px"
									>
										<Box
											as="button"
											onClick={handlePlayPause}
											color="white"
											cursor="pointer"
											flexShrink={0}
											_hover={{ opacity: 0.8 }}
										>
											<Box as={isPlaying ? FaPause : FaPlay} fontSize="14px" ml={isPlaying ? '0' : '2px'} />
										</Box>
										<Box
											ref={seekBarRef}
											flex={1}
											h="6px"
											bg="whiteAlpha.300"
											borderRadius="full"
											cursor="pointer"
											onClick={handleSeek}
											position="relative"
										>
											<Box
												h="100%"
												bg="white"
												borderRadius="full"
												ref={progressBarRef}
												w="0%"
											/>
										</Box>
										<Text ref={timeDisplayRef} color="white" fontSize="xs" flexShrink={0} whiteSpace="nowrap" display={{ base: 'none', sm: 'block' }}>
											0:00 / 0:00
										</Text>
										<Box
											as="button"
											onClick={() => setIsExpanded(true)}
											color="white"
											cursor="pointer"
											flexShrink={0}
											_hover={{ opacity: 0.8 }}
										>
											<Box as={FaExpand} fontSize="12px" />
										</Box>
									</Flex>
								)}
							</>
						) : (
							<Flex w="100%" h="100%" align="center" justify="center" bg="gray.100" borderRadius="5px">
								<FusionLoading isLoading />
							</Flex>
						)}
					</Box>
					<Box px={6} borderBottomRadius="lg" mb={4} mt={4}>
						<Text
							data-testid="creative-headline"
							fontWeight="bold"
							noOfLines={1}
							size="md"
							mb={2}
						>
							{headline}
						</Text>
						<Text fontSize="sm" mb={4}>
							{description}
						</Text>
						<Button
							variant="orangeSolid"
							borderRadius="3xl"
							w="full"
							pointerEvents="none"
						>
							{formatCallToAction(CTA)}
						</Button>
					</Box>
				</>
			)}
		</CreativeCard>
		{isExpanded && videoTemplate && (
			<VideoExpandModal
				isOpen={isExpanded}
				onClose={() => setIsExpanded(false)}
				template={videoTemplate}
				creativeId={creative.id}
			/>
		)}
		</>
	);
};

export default VideoSquareCreative;
